import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiArrowLeft } from "react-icons/fi";
import { Header } from "../../components/Header";
import { NotFoundCard } from "../../components/NotFoundCard";
import { NotFound } from "../../components/NotFound";
import { useConfiguration } from "../../contexts/ConfigurationProvider";
import { getLogoNavigationTarget } from "../../utils/logoNavigation";

/**
 * 404 for an unmatched path under a site that *does* resolve (`/kantarell/abc`).
 * Because the slug is real we can show the site's own chrome and offer a
 * destination, which the chromeless NotFound cannot: it renders outside the
 * router, so it has no site to return to.
 *
 * The search bar stays enabled deliberately — it is the one recovery path on a
 * hotel 404 that does not depend on the guest knowing who to contact.
 *
 * No min-height here: RootLayout is already `min-h-screen` with a `flex-1`
 * main, so forcing a viewport height on top of that pushes its footer a full
 * screen below the fold.
 */
export const SiteNotFound = () => {
  const { t } = useTranslation();
  const { identifier } = useParams();
  const location = useLocation();
  const { configuration } = useConfiguration();

  // `/:identifier/*` also matches `/p/<id>/<junk>`, where ConfigurationProvider
  // is resolving a payment link rather than a site. That path hard-redirects to
  // /404 on failure, but until it does there is no site chrome to render.
  if (!configuration) return <NotFound />;

  // Same destination as the header logotype, including the per-site setting
  // that sends it to the hotel's own website. A plain anchor covers both the
  // internal path and the external URL, and matches the logo's full page load.
  const homeHref = getLogoNavigationTarget(
    identifier,
    configuration.logotypeLinksToWebsite,
    configuration.siteUrl,
    location.search,
  );

  return (
    <div className="flex flex-col bg-surface-page">
      <Header hideCart />
      <div className="flex items-center justify-center px-4 py-12">
        <NotFoundCard
          title={t("error.not_found.page.title")}
          description={t("error.not_found.page.description")}
          action={
            <a
              href={homeHref}
              className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full bg-primary px-5 py-3 text-sm font-medium text-on-primary transition-all duration-200 hover:scale-[1.02] hover:shadow-md active:scale-95"
            >
              <FiArrowLeft className="h-4 w-4" />
              {t("error.not_found.back_home")}
            </a>
          }
        />
      </div>
    </div>
  );
};
